import { Quaternion, Vector3 } from 'three';

export interface RemotePlayerState {
  id: string;
  name: string;
  position: Vector3;
  quaternion: Quaternion;
  throttle: number;
  health: number;
  score: number;
  alive: boolean;
  respawnAt: number;
  flareUntil: number;
  updatedAt: number;
}

export interface SelfPlayerState {
  id: string;
  name: string;
  health: number;
  score: number;
  alive: boolean;
  respawnAt: number;
  flareUntil: number;
}

export interface RemoteMissileState {
  id: string;
  ownerId: string;
  targetId: string | null;
  faction: 'player' | 'enemy';
  position: Vector3;
  direction: Vector3;
  speed: number;
  updatedAt: number;
}

type Listener<T> = (value: T) => void;

export class MultiplayerClient {
  private socket: WebSocket | null = null;
  private remote = new Map<string, RemotePlayerState>();
  private missiles = new Map<string, RemoteMissileState>();
  private self: SelfPlayerState | null = null;
  private selfId: string | null = null;
  private room = 'HK';
  private name = '';
  private connected = false;
  private manualClose = false;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private pongTimeoutTimer: number | null = null;
  private connectTimeoutTimer: number | null = null;
  private connectionEpoch = 0;

  private listeners = {
    connection: new Set<Listener<boolean>>(),
    message: new Set<Listener<string>>(),
    event: new Set<Listener<any>>(),
  };

  connect(room: string, name: string): void {
    this.disconnect();
    this.manualClose = false;
    this.clearTimers();
    const epoch = ++this.connectionEpoch;
    this.room = room || 'HK';
    this.name = name || 'Pilot';
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${location.host}/realtime?room=${encodeURIComponent(this.room)}&name=${encodeURIComponent(this.name)}`);
    this.socket = socket;
    this.connectTimeoutTimer = window.setTimeout(() => {
      if (this.connectionEpoch !== epoch || this.socket !== socket) return;
      if (socket.readyState === WebSocket.CONNECTING) {
        this.emit('message', '香港节点连接超时。');
        socket.close(4000, 'Connect timeout');
      }
    }, 12000);
    socket.onopen = () => {
      if (this.connectionEpoch !== epoch || this.socket !== socket) return;
      if (this.connectTimeoutTimer !== null) {
        window.clearTimeout(this.connectTimeoutTimer);
        this.connectTimeoutTimer = null;
      }
      this.connected = true;
      this.reconnectAttempts = 0;
      this.emit('connection', true);
      this.emit('message', '香港节点接入完成。');
      this.startHeartbeat();
    };
    socket.onclose = () => {
      if (this.connectionEpoch !== epoch || this.socket !== socket) return;
      if (this.connectTimeoutTimer !== null) {
        window.clearTimeout(this.connectTimeoutTimer);
        this.connectTimeoutTimer = null;
      }
      this.connected = false;
      this.clearHeartbeat();
      this.emit('connection', false);
      this.emit('message', '香港节点连接中断。');
      this.remote.clear();
      this.missiles.clear();
      if (!this.manualClose) this.scheduleReconnect();
    };
    socket.onerror = () => {
      if (this.connectionEpoch !== epoch || this.socket !== socket) return;
      if (this.connectTimeoutTimer !== null) {
        window.clearTimeout(this.connectTimeoutTimer);
        this.connectTimeoutTimer = null;
      }
      if (!this.connected) this.emit('connection', false);
      this.emit('message', '香港节点连接异常。');
      if (socket.readyState === WebSocket.CONNECTING) socket.close();
    };
    socket.onmessage = (event) => {
      if (this.connectionEpoch !== epoch || this.socket !== socket) return;
      this.handleMessage(event.data);
    };
  }

  disconnect(): void {
    this.manualClose = true;
    this.clearTimers();
    if (this.socket) {
      const closing = this.socket;
      this.socket = null;
      try {
        closing.onopen = null;
        closing.onclose = null;
        closing.onerror = null;
        closing.onmessage = null;
        closing.close();
      } catch {
        // ignore close race
      }
    }
    this.connected = false;
    this.selfId = null;
    this.self = null;
    this.remote.clear();
    this.missiles.clear();
  }

  onConnection(listener: Listener<boolean>): () => void {
    this.listeners.connection.add(listener);
    return () => this.listeners.connection.delete(listener);
  }

  onMessage(listener: Listener<string>): () => void {
    this.listeners.message.add(listener);
    return () => this.listeners.message.delete(listener);
  }

  onEvent(listener: Listener<any>): () => void {
    this.listeners.event.add(listener);
    return () => this.listeners.event.delete(listener);
  }

  isConnected(): boolean {
    return this.connected;
  }

  getRemotePlayers(): RemotePlayerState[] {
    const now = Date.now();
    for (const [id, state] of this.remote) {
      if (now - state.updatedAt > 9000) this.remote.delete(id);
    }
    return [...this.remote.values()];
  }

  getSelfState(): SelfPlayerState | null {
    return this.self;
  }

  getMissiles(): RemoteMissileState[] {
    const now = Date.now();
    for (const [id, missile] of this.missiles) {
      if (now - missile.updatedAt > 4000) this.missiles.delete(id);
    }
    return [...this.missiles.values()];
  }

  sendState(position: Vector3, quaternion: Quaternion, throttle: number): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({
      type: 'state',
      position: { x: position.x, y: position.y, z: position.z },
      quaternion: { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w },
      throttle,
      ts: Date.now(),
    }));
  }

  sendCombat(weapon: 'missile' | 'cannon' | 'flare', targetId?: string | null): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({
      type: 'combat',
      weapon,
      targetId: targetId ?? null,
      ts: Date.now(),
    }));
  }

  sendChat(text: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({
      type: 'chat',
      text: text.slice(0, 120),
      ts: Date.now(),
    }));
  }

  private handleMessage(raw: string): void {
    let message: any;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }
    if (message.type === 'welcome') {
      this.selfId = message.id;
      this.self = {
        id: message.id,
        name: this.name,
        health: 100,
        score: 0,
        alive: true,
        respawnAt: 0,
        flareUntil: 0,
      };
      this.emit('message', `联机识别完成 ${message.id}`);
      return;
    }
    if (message.type === 'snapshot' && Array.isArray(message.players)) {
      const now = Date.now();
      const activePlayers = new Set<string>();
      for (const player of message.players) {
        if (!player?.id) continue;
        activePlayers.add(player.id);
        if (player.id === this.selfId) {
          this.self = {
            id: player.id,
            name: player.name || this.name,
            health: Number(player.health ?? 100),
            score: Number(player.score ?? 0),
            alive: Boolean(player.alive ?? true),
            respawnAt: Number(player.respawnAt ?? 0),
            flareUntil: Number(player.flareUntil ?? 0),
          };
          continue;
        }
        this.remote.set(player.id, {
          id: player.id,
          name: player.name || `Pilot ${player.id}`,
          position: new Vector3(player.position?.x ?? 0, player.position?.y ?? 0, player.position?.z ?? 0),
          quaternion: new Quaternion(player.quaternion?.x ?? 0, player.quaternion?.y ?? 0, player.quaternion?.z ?? 0, player.quaternion?.w ?? 1),
          throttle: Number(player.throttle ?? 0.7),
          health: Number(player.health ?? 100),
          score: Number(player.score ?? 0),
          alive: Boolean(player.alive ?? true),
          respawnAt: Number(player.respawnAt ?? 0),
          flareUntil: Number(player.flareUntil ?? 0),
          updatedAt: now,
        });
      }
      for (const id of [...this.remote.keys()]) {
        if (!activePlayers.has(id)) this.remote.delete(id);
      }
      const activeMissiles = new Set<string>();
      if (Array.isArray(message.missiles)) {
        for (const missile of message.missiles) {
          if (!missile?.id) continue;
          activeMissiles.add(missile.id);
          this.missiles.set(missile.id, {
            id: missile.id,
            ownerId: missile.ownerId || '',
            targetId: missile.targetId ?? null,
            faction: missile.faction === 'enemy' ? 'enemy' : 'player',
            position: new Vector3(missile.position?.x ?? 0, missile.position?.y ?? 0, missile.position?.z ?? 0),
            direction: new Vector3(missile.direction?.x ?? 0, missile.direction?.y ?? 0, missile.direction?.z ?? -1),
            speed: Number(missile.speed ?? 0),
            updatedAt: now,
          });
        }
      }
      for (const id of [...this.missiles.keys()]) {
        if (!activeMissiles.has(id)) this.missiles.delete(id);
      }
      return;
    }
    if (message.type === 'event') {
      this.emit('event', message);
      if (typeof message.text === 'string') this.emit('message', message.text);
      return;
    }
    if (message.type === 'pong') {
      if (this.pongTimeoutTimer !== null) {
        window.clearTimeout(this.pongTimeoutTimer);
        this.pongTimeoutTimer = null;
      }
      return;
    }
    if (message.type === 'left' && message.id) {
      this.remote.delete(message.id);
      for (const [missileId, missile] of this.missiles) {
        if (missile.ownerId === message.id || missile.targetId === message.id) this.missiles.delete(missileId);
      }
      this.emit('message', `${message.id} 已脱离空域`);
    }
  }

  private emit<K extends keyof MultiplayerClient['listeners']>(key: K, value: Parameters<Listener<any>>[0]): void {
    const listeners = this.listeners[key] as Set<Listener<any>>;
    for (const listener of listeners) listener(value);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.pingTimer = window.setInterval(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      this.socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
      if (this.pongTimeoutTimer !== null) window.clearTimeout(this.pongTimeoutTimer);
      this.pongTimeoutTimer = window.setTimeout(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) this.socket.close(4001, 'Heartbeat timeout');
      }, 22000);
    }, 15000);
  }

  private clearHeartbeat(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongTimeoutTimer !== null) {
      window.clearTimeout(this.pongTimeoutTimer);
      this.pongTimeoutTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const waitMs = Math.min(12000, 1500 + this.reconnectAttempts * 1500);
    this.reconnectAttempts += 1;
    this.emit('message', `正在重连 HK 节点（${Math.ceil(waitMs / 1000)}s）…`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.room, this.name);
    }, waitMs);
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearHeartbeat();
    if (this.connectTimeoutTimer !== null) {
      window.clearTimeout(this.connectTimeoutTimer);
      this.connectTimeoutTimer = null;
    }
  }
}
